import { Box, Button, Stack } from "@mantine/core"
import { useEffect, useRef, useState } from "react"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { RouterLink } from "atlas_forecasting_app/lib/router"
import { QuestionForecastCard } from "atlas_forecasting_app/components/forecast/question_forecast_card"
import { EmptyState, ErrorAlert, LoadingState, PageHeader } from "atlas_forecasting_app/components/common/page_elements"

export function MyQuestionsPage() {
  const [items, setItems] = useState([])
  const [cursor, setCursor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const sentinelRef = useRef(null)

  const loadMore = async () => {
    if (loading) return
    setLoading(true)
    try {
      const qs = new URLSearchParams()
      qs.set("limit", "20")
      if (cursor) qs.set("cursor", cursor)
      const data = await apiFetch(`/api/me/questions?${qs.toString()}`)
      const next = data.items || data.questions || []
      setItems((prev) => prev.concat(next))
      setCursor(data.next_cursor || null)
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && cursor) loadMore()
      },
      { rootMargin: "600px 0px" },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [cursor, loading])

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow="Workspace"
        variant="editorial"
        title="My questions"
        subtitle="Every question you’ve added, with the latest forecast context alongside it."
        action={
          <Button component={RouterLink} to="/draft-questions" variant="light">
            Draft Questions
          </Button>
        }
      />

      <ErrorAlert message={error} />

      {!loading && !error && items.length === 0 ? (
        <EmptyState
          title="No questions yet"
          message="Questions you create or import will appear here with their latest forecast context."
          action={
            <Button component={RouterLink} to="/new-question">
              Create your first question
            </Button>
          }
        />
      ) : null}

      <Stack gap="md" className="af-card-list">
        {items.map((item) => (
          <QuestionForecastCard key={item?.question?.id || item?.latest_forecast?.id} item={item} variant="my_questions" />
        ))}
        <Box ref={sentinelRef}>{loading ? <LoadingState label="Loading more questions..." /> : null}</Box>
      </Stack>
    </Stack>
  )
}
